import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPlanCuentas, ejecutarMayorizacion } from '../../reducers/planCuentas_slice';
import { Table, Modal, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { fechaActualBoliviaOnly } from '../../util/valueCalculator';
import { BiShow, BiHide, BiPrinter, BiRefresh, BiArrowBack } from 'react-icons/bi';
import CustomPrintModal from '../UI/CustomPrintModal';
import BalanceGeneralModal from './BalanceGeneralModal';
import moment from 'moment-timezone';

// ✅ NUEVO: modal externo reusable (misma carpeta)
import MovimientosCuentaModal from './MovimientosCuentaModal';

const PlanCuentasView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const planCuentas = useSelector((state) => state.planCuentas.planCuentas || []);

  const fecha = moment().tz("America/La_Paz").format('YYYY-MM-DD');
  const currentYear = new Date().getFullYear();
  const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');
  const currentDay = String(new Date().getDate()).padStart(2, '0');
  
  const [startDate, setStartDate] = useState(fecha);
  const [endDate, setEndDate] = useState(fecha);

  const [expandedCuentas, setExpandedCuentas] = useState({});
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [fechaInicio, setFechaInicio] = useState(fecha);
  const [fechaFinal, setFechaFinal] = useState(fecha);
  const [fechaBase, setFechaBase] = useState({ ini: fecha, fin: fecha });
  const [canUpdate, setCanUpdate] = useState(false);
  const [includeZeroBalances, setIncludeZeroBalances] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [datosActualizados, setDatosActualizados] = useState(false);

  // ✅ NUEVO: estado para abrir/cerrar modal y enviar cuenta elegida
  const [showMovimientosModal, setShowMovimientosModal] = useState(false);
  const [cuentaElegida, setCuentaElegida] = useState(null);

  useEffect(() => {
    dispatch(fetchPlanCuentas());
  }, [dispatch]);

  const handleExpandToggle = (id) => {
    setExpandedCuentas(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDateChange = () => {
    const fechasCambiadas = fechaInicio !== fechaBase.ini || fechaFinal !== fechaBase.fin;
    setCanUpdate(fechasCambiadas);
    if (fechasCambiadas) {
      setDatosActualizados(false);
    }
  };

  // ==========================================================
  // SOLO FECHAS:
  // - Misma gestión (mismo año)
  // - Fecha Final >= Fecha Inicio
  // - Sin problemas de zona horaria: comparar strings YYYY-MM-DD
  // ==========================================================
  const getYearFromYMD = (ymd) => {
    if (!ymd || typeof ymd !== 'string' || ymd.length < 4) return null;
    const y = parseInt(ymd.slice(0, 4), 10);
    return Number.isFinite(y) ? y : null;
  };

  const forceYear = (ymd, targetYear) => {
    if (!ymd || !targetYear) return ymd;
    const mmdd = ymd.slice(5); // "MM-DD"
    const candidate = `${targetYear}-${mmdd}`;
    // 29/02 en no bisiesto
    if (mmdd === '02-29') return `${targetYear}-02-28`;
    return candidate;
  };

  const handleFechaInicioChange = (value) => {
    let nuevaIni = value;
    let nuevaFin = fechaFinal;

    const yIni = getYearFromYMD(nuevaIni);
    const yFin = getYearFromYMD(nuevaFin);

    // Misma gestión
    if (yIni && yFin && yFin !== yIni) {
      nuevaFin = forceYear(nuevaFin, yIni);
      Swal.fire(
        'Atención',
        'La Fecha Final debe estar en la misma gestión que la Fecha Inicio. Se ajustó automáticamente.',
        'info'
      );
    }

    // Final >= Inicio (comparación segura por string YYYY-MM-DD)
    if (nuevaFin && nuevaIni && nuevaFin < nuevaIni) {
      nuevaFin = nuevaIni;
      Swal.fire(
        'Atención',
        'La Fecha Final no puede ser menor que la Fecha Inicio. Se ajustó automáticamente.',
        'info'
      );
    }

    setFechaInicio(nuevaIni);
    if (nuevaFin !== fechaFinal) setFechaFinal(nuevaFin);
    handleDateChange();
  };

  const handleFechaFinalChange = (value) => {
    let nuevaFin = value;

    if (!fechaInicio) {
      setFechaFinal(nuevaFin);
      handleDateChange();
      return;
    }

    const yIni = getYearFromYMD(fechaInicio);
    const yFin = getYearFromYMD(nuevaFin);

    // Misma gestión
    if (yIni && yFin && yFin !== yIni) {
      nuevaFin = forceYear(nuevaFin, yIni);
      Swal.fire(
        'Atención',
        'La Fecha Final debe estar en la misma gestión que la Fecha Inicio. Se ajustó automáticamente.',
        'info'
      );
    }

    // Final >= Inicio
    if (nuevaFin && fechaInicio && nuevaFin < fechaInicio) {
      nuevaFin = fechaInicio;
      Swal.fire(
        'Atención',
        'La Fecha Final no puede ser menor que la Fecha Inicio. Se ajustó automáticamente.',
        'info'
      );
    }

    setFechaFinal(nuevaFin);
    handleDateChange();
  };

  const handleUpdate = async () => {
    setShowUpdateModal(false);
    Swal.fire({
      title: 'Actualizando...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
  
    try {
      await dispatch(ejecutarMayorizacion({ fechaIni: fechaInicio, fechaFin: fechaFinal })).unwrap();
      await dispatch(fetchPlanCuentas()).unwrap();
      setFechaBase({ ini: fechaInicio, fin: fechaFinal });
      setCanUpdate(false);
      setDatosActualizados(true);
      Swal.fire('Éxito', 'Datos actualizados correctamente', 'success');
    } catch (error) {
      console.log("Error=", error);
      setDatosActualizados(false);
      Swal.fire('Error', 'No se pudo actualizar la mayorización', 'error');
    }
  };

  const buildHierarchy = useCallback((cuentas, parentId = null) => {
    return cuentas
      .filter(cuenta => cuenta.id_padre === parentId)
      .map(cuenta => ({
        ...cuenta,
        subcuentas: buildHierarchy(cuentas, cuenta.id)
      }));
  }, []);

  const cuentasHierarchy = useMemo(() => {
    return buildHierarchy(planCuentas);
  }, [planCuentas, buildHierarchy]);

  const applyZeroBalanceFilter = useCallback((cuentas) => {
    return cuentas
      .map(cuenta => ({
        ...cuenta,
        subcuentas: applyZeroBalanceFilter(cuenta.subcuentas || [])
      }))
      .filter(cuenta => 
        ['01', '02', '03'].includes(cuenta.codigo_cuenta) || 
        includeZeroBalances || 
        cuenta.debe != 0 || 
        cuenta.haber != 0 || 
        (cuenta.subcuentas && cuenta.subcuentas.length > 0)
      );
  }, [includeZeroBalances]);

  const filteredHierarchy = useMemo(() => {
    return applyZeroBalanceFilter(cuentasHierarchy);
  }, [cuentasHierarchy, applyZeroBalanceFilter]);

  // ✅ NUEVO: handler para abrir modal externo
  const handleOpenMovimientos = (cuenta) => {
    setCuentaElegida(cuenta);
    setShowMovimientosModal(true);
  };

  const renderCuentas = (cuentas, level = 0) => {
    return cuentas.map(cuenta => (
      <React.Fragment key={cuenta.id}>
        <tr>
          <td style={{ paddingLeft: `${level * 20}px` }}>
            {cuenta.subcuentas.length > 0 && (
              <button onClick={() => handleExpandToggle(cuenta.id)} className="icon-button btn-sm">
                {expandedCuentas[cuenta.id] ? <i className="bi bi-chevron-up"></i> : <i className="bi bi-chevron-down"></i>}
              </button>
            )}
            {cuenta.codigo_cuenta}
          </td>
          <td>{cuenta.nombre_cuenta}</td>
          <td className="text-end">{parseFloat(cuenta.debe).toFixed(2)}</td>
          <td className="text-end">{parseFloat(cuenta.haber).toFixed(2)}</td>
          <td className="text-end">{parseFloat(cuenta.saldo).toFixed(2)}</td>

          {/* ✅ NUEVO: Acción Movimientos (solo cuentas de detalle) */}
          <td className="text-center">
            {(!cuenta.subcuentas || cuenta.subcuentas.length === 0) && (
              <button
                className="btn btn-sm btn-outline-info"
                onClick={() => handleOpenMovimientos(cuenta)}
                disabled={!datosActualizados}
                title="Ver movimientos (Detalle de Comprobantes)"
              >
                Movimientos
              </button>
            )}
          </td>
        </tr>
        {expandedCuentas[cuenta.id] && renderCuentas(cuenta.subcuentas, level + 1)}
      </React.Fragment>
    ));
  };

  const buildExportData = (cuentas, level = 0) => {
    let result = [];
    cuentas.forEach(cuenta => {
      result.push({
        Código: cuenta.codigo_cuenta,
        Nombre: cuenta.nombre_cuenta,
        Debe: parseFloat(cuenta.debe).toFixed(2),
        Haber: parseFloat(cuenta.haber).toFixed(2),
        Saldo: parseFloat(cuenta.saldo).toFixed(2),
        Nivel: level
      });
      if (cuenta.subcuentas && cuenta.subcuentas.length > 0) {
        result = result.concat(buildExportData(cuenta.subcuentas, level + 1));
      }
    });
    return result;
  };

  const handleShowPreview = () => {
    const data = buildExportData(filteredHierarchy);
    const content = {
      head: [['Código', 'Nombre', 'Debe', 'Haber', 'Saldo']],
      body: data.map(row => [row.Código, row.Nombre, row.Debe, row.Haber, row.Saldo])
    };
    setPrintContent(content);
    setShowPreviewModal(true);
  };

  return (
  <div className="list-container list1" style={{ maxHeight: 'none', height: 'auto', overflow: 'visible' }}>

      <h1 className="mb-4">Plan de Cuentas</h1>

      <div className="mb-4">
        <div className="d-flex align-items-center gap-3">
          <button onClick={() => navigate('/admin')} className="btn btn-outline-secondary">
            <BiArrowBack /> Volver
          </button>

          <div className="d-flex align-items-center gap-2">
            <label>Fecha Inicio:
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => {
                  handleFechaInicioChange(e.target.value);
                }}
                className="form-control ms-2"
              />
            </label>

            <label>Fecha Final:
              <input
                type="date"
                value={fechaFinal}
                onChange={(e) => {
                  handleFechaFinalChange(e.target.value);
                }}
                className="form-control ms-2"
              />
            </label>
          </div>

          <button
            onClick={() => setShowUpdateModal(true)}
            className="btn btn-outline-primary"
            disabled={!canUpdate}
          >
            <BiRefresh /> Actualizar
          </button>

          <button
            onClick={() => setIncludeZeroBalances(!includeZeroBalances)}
            className="btn btn-outline-secondary"
            disabled={!datosActualizados}
          >
            {includeZeroBalances ? <BiShow /> : <BiHide />}
            {includeZeroBalances ? " Ocultar ceros" : " Mostrar ceros"}
          </button>

          <button 
            onClick={handleShowPreview} 
            className="btn btn-outline-success"
            disabled={!datosActualizados}
          >
            <BiPrinter /> Plan de Cuentas Mayorizado
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <Table bordered hover>
          <thead className="table-dark">
            <tr>
              <th>Código</th>
              <th>Nombre</th>
              <th className="text-end">Debe</th>
              <th className="text-end">Haber</th>
              <th className="text-end">Saldo</th>
              {/* ✅ NUEVO */}
              <th className="text-center">Acción</th>
            </tr>
          </thead>
          <tbody>
            {renderCuentas(filteredHierarchy)}
          </tbody>
        </Table>
      </div>

      <div className="text-end mt-3">
        <Button
          variant="dark"
          onClick={() => setShowBalanceModal(true)}
          disabled={canUpdate || !datosActualizados}
        >
          Balance General
        </Button>
      </div>

      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Actualización</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Está seguro de actualizar los saldos con los comprobantes del rango seleccionado?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleUpdate}>
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      <CustomPrintModal
        show={showPreviewModal}
        handleClose={() => setShowPreviewModal(false)}
        title1="PLAN DE CUENTAS MAYORIZADO"
        title2={`Del ${fechaInicio} al ${fechaFinal}`}
        content={printContent}
        copia={false}
      />

      <BalanceGeneralModal
        show={showBalanceModal}
        handleClose={() => setShowBalanceModal(false)}
        cuentasMap={filteredHierarchy}
        fechaInicio={fechaInicio}
        fechaFinal={fechaFinal}
      />

      {/* ✅ NUEVO: Modal externo reusable */}
      <MovimientosCuentaModal
        show={showMovimientosModal}
        onHide={() => setShowMovimientosModal(false)}
        cuenta={cuentaElegida}
        fechaIni={fechaInicio}
        fechaFin={fechaFinal}
      />
    </div>
  );
};

export default PlanCuentasView;
